import type { FilterDef, FilterOption } from "@/lib/table"

import { REVIEW_STATUS_LABEL, REVIEW_STATUS_ORDER } from "./queue-review-status"

/*
   Queue filter schema — declarative. Consumed by <FilterButton>,
   <FilterPanel> and <ActiveFilterChips>; `buildThreadsParams`
   (queue-query-params.ts) turns the values into the request. Adding a filter
   is one entry here plus one branch there.

   Each `id` is also its URL parameter (lib/table/use-table-url-state.ts), so the ids
   are the legacy queue's own: `status`, `findings`, `unread`, `archived`. A link copied
   from the legacy queue opens the same view here. `assignee` and `updated`
   are new: legacy had only a "Mine" toggle and no date range.

   Every filter here is applied by the server, so it narrows the whole queue
   rather than the page on screen.

   `assigneeOptions` comes from `assigneeFilterOptions` (depends on who is
   signed in); `null` leaves the Assignee filter out of the panel entirely.
*/

export const QUEUE_FILTER_IDS = {
  status: "status",
  assignee: "assignee",
  findings: "findings",
  unread: "unread",
  archived: "archived",
  updated: "updated",
} as const

export function buildQueueFilterSchema(assigneeOptions: FilterOption[] | null = null): FilterDef[] {
  return [
    {
      id: QUEUE_FILTER_IDS.status,
      label: "Status",
      kind: "select",
      options: REVIEW_STATUS_ORDER.map((s) => ({ value: s, label: REVIEW_STATUS_LABEL[s] })),
      placeholder: "Any status",
    },
    ...(assigneeOptions
      ? [
          {
            id: QUEUE_FILTER_IDS.assignee,
            label: "Assignee",
            kind: "select",
            multi: true,
            options: assigneeOptions,
          } satisfies FilterDef,
        ]
      : []),
    {
      id: QUEUE_FILTER_IDS.findings,
      label: "Findings",
      kind: "select",
      options: [
        { value: "outstanding", label: "Has outstanding" },
        { value: "corrected", label: "All corrected" },
        { value: "none", label: "No findings yet" },
      ],
      placeholder: "Any findings",
    },
    {
      id: QUEUE_FILTER_IDS.unread,
      label: "Comms",
      kind: "boolean",
      hint: "Only threads with unread messages",
    },
    {
      // Off by default, as in legacy: an archived product is off the queue until asked for.
      id: QUEUE_FILTER_IDS.archived,
      label: "Include archived",
      kind: "boolean",
      hint: "Archived products are hidden by default",
    },
    {
      id: QUEUE_FILTER_IDS.updated,
      label: "Updated",
      kind: "date-range",
    },
  ]
}
